/* eslint-disable no-mixed-spaces-and-tabs */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import React, {
	ReactNode,
	useState,
	useEffect,
} from 'react';

// Style
import classes from './MenuSelectFilter.module.css';

// Mantine
import {
	Popover,
	Radio,
	Box,
	TextInput,
	Stack,
	Button,
	Text,
	Image,
	ActionIcon,
	Tooltip,
} from '@mantine/core';
import {
	useClickOutside,
	useFocusTrap,
	useMediaQuery,
} from '@mantine/hooks';

import { Tooltip as CustomTooltip } from '@components/atoms/Tooltip';

// Helpers
import {
	capitalizeString,
	truncateString,
} from '@utils/functions';

// Icons
import SearchIcon from '@assets/icons/search.svg';
import ArrowDownIcon from '@assets/icons/arrow-down.svg';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

// Type
type Item = {
	label: string;
	value: any;
};

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const MenuSelectFilter = (props: {
	onApply: (item: Item[]) => void;
	onChange?: (item: Item) => void;
	children?: ReactNode;
	size?: string;
	variant?: string;
	placeholder?: string;
	color?: string;
	label?: string;
	options: Item[] | null;
	fullWidth?: boolean;
	loading?: boolean;
	disabled?: boolean;
	leftSection?: React.ReactNode;
	reset?: boolean;
	isPersist?: boolean;
	value?: any;
	opened?: boolean;
	withoutSearch?: boolean;
	handlereset?: (_isReset: boolean) => void;
}) => {
	/* ******************************** Media Query ******************************* */
	const isBigScreen = useMediaQuery('(min-width: 120em)');
	/* ******************************** States ******************************* */
	const [selectedItems, setSelectedItems] = useState<
		Item[]
	>([]);
	const [appliedItems, setAppliedItems] = useState<
		Item[]
	>([]);
	const [value, setValue] = useState<null | string>(null);

	const [searchTerm, setSearchTerm] =
		useState<string>('');

	/* ******************************** RENDERING ******************************* */
	const { t } = useTranslation();

	/* ******************************** Variables ******************************* */
	const totalSelectedItems = selectedItems.length;
	const filteredOptions = props.options
		? props.options.filter((item: { label: string }) =>
				t(item.label)
					.toLowerCase()
					.includes(searchTerm.toLowerCase())
			)
		: [];
	/* ******************************** Helpers ******************************* */
	const handleRemoveItems = () => {
		setSelectedItems(appliedItems);
		if (
			appliedItems &&
			Array.isArray(appliedItems) &&
			appliedItems.length > 0 &&
			appliedItems[0].label
		)
			setValue(appliedItems[0].label);
	};
	const focusTrapRef = useFocusTrap();
	const refDropdown = useClickOutside(handleRemoveItems);

	const handleApply = () => {
		const updatedItems = [...selectedItems].map(
			(item) => ({ ...item })
		);
		setAppliedItems(updatedItems);
		setSelectedItems(updatedItems);
		props.onApply(selectedItems);
	};

	const handleCheckboxChange = (_label: string) => {
		setValue(_label);
		setSelectedItems(
			filteredOptions.filter(
				(item) => item.label == _label
			)
		);
	};

	useEffect(() => {
		if (props && props.handlereset && props.reset) {
			setSelectedItems([]);
			setAppliedItems([]);
			props.handlereset(false);
		}
	}, [props.reset]);

	useEffect(() => {
		if (props.isPersist && props.value) {
			const objetsFiltres = props.options
				? props.options.filter((objet) =>
						props.value.includes(objet.value)
					)
				: [];
			setSelectedItems(objetsFiltres);
		}
	}, [props.isPersist]);

	const handleClearSelectedItems = () => {
		setAppliedItems([]);
		setSelectedItems([]);
		setValue(null);
		props.onApply([]);
	};

	/* ******************************** RENDERING ******************************* */
	return (
		<Popover
			data-testid="M00G1R8_Z5jdHi6Q-component"
			width={'target'}
			position="bottom"
			classNames={classes}
			clickOutsideEvents={['mouseup', 'touchend']}
			disabled={props.disabled}
			opened={props.opened}
		>
			<CustomTooltip
				label={
					selectedItems.length == 1 &&
					selectedItems[0].label.length > 19
						? selectedItems[0].label
						: ''
				}
				isActive={
					selectedItems.length == 1 &&
					selectedItems[0].label.length > 19
				}
			>
				<Popover.Target>
					<Box
						size={props.size}
						className={clsx(
							!props.disabled
								? classes.containerTarget
								: classes.containerTargetDisabled,
							totalSelectedItems.toString() !== '0' &&
								classes.containerTargetSelected
						)}
						variant="outline"
					>
						{totalSelectedItems.toString() == '0'
							? props.label
							: selectedItems.length > 1
								? capitalizeString(props.label) +
									' (' +
									totalSelectedItems.toString() +
									')'
								: truncateString(
										t(selectedItems[0].label),
										19
									)}

						<ActionIcon
							size={'12px'}
							variant="transparent"
						>
							<Image
								className={clsx(
									classes.containerIconUp,
									props.disabled &&
										classes.containerIconUpDisabled
								)}
								src={ArrowDownIcon}
							/>
						</ActionIcon>
					</Box>
				</Popover.Target>
			</CustomTooltip>
			<Popover.Dropdown ref={refDropdown}>
				{!props.withoutSearch && (
					<TextInput
						ref={focusTrapRef}
						data-testid="D1CtFXwC_F5jdHi65-input"
						size={isBigScreen ? 'sm' : 'xs'}
						value={searchTerm}
						onChange={(e) =>
							setSearchTerm(e.target.value)
						}
						leftSection={
							<Image
								className={classes.containerSearchIcon}
								src={SearchIcon}
							/>
						}
						placeholder={props.placeholder}
						classNames={{
							input: classes.textSearch,
						}}
					/>
				)}

				<Box className={classes.containerOptions}>
					<Radio.Group
						visibleFrom="xxl"
						value={value}
						onChange={(data) => {
							handleCheckboxChange(data);
						}}
					>
						<Stack
							gap={'md'}
							style={{
								padding: '0px 5px',
							}}
						>
							{props &&
							filteredOptions &&
							Array.isArray(filteredOptions) &&
							filteredOptions.length > 0 ? (
								filteredOptions.map((item: any) => (
									<Box key={item.value}>
										<Tooltip
											label={t(item.label)}
											refProp={
												t(item.label).length > 20
													? 'rootRef'
													: ''
											}
										>
											<Radio
												data-testid={'xxl' + item.label}
												visibleFrom="xxl"
												size="sm"
												classNames={{
													label: classes.textOption,
												}}
												label={truncateString(
													t(item.label),
													20
												)}
												value={item.label}
											/>
										</Tooltip>
									</Box>
								))
							) : (
								<Text className={classes.emptyItemsText}>
									{t('global.noData')}
								</Text>
							)}
						</Stack>
					</Radio.Group>
					<Radio.Group
						hiddenFrom="xxl"
						value={value}
						onChange={(data) => {
							handleCheckboxChange(data);
						}}
					>
						<Stack
							gap={'md'}
							style={{
								padding: '0px 5px',
							}}
						>
							{props &&
							filteredOptions &&
							Array.isArray(filteredOptions) &&
							filteredOptions.length > 0 ? (
								filteredOptions.map((item: any) => (
									<Box key={item.value}>
										<Tooltip
											label={t(item.label)}
											refProp={
												t(item.label).length > 12
													? 'rootRef'
													: ''
											}
										>
											<Radio
												data-testid={item.label}
												hiddenFrom="xxl"
												size="xs"
												classNames={{
													label: classes.textOption,
												}}
												label={truncateString(
													t(item.label),
													12
												)}
												value={item.label}
											/>
										</Tooltip>
									</Box>
								))
							) : (
								<Text className={classes.emptyItemsText}>
									{t('global.noData')}
								</Text>
							)}
						</Stack>
					</Radio.Group>
				</Box>
				<Box className={classes.containerButton}>
					<Button
						onClick={handleApply}
						visibleFrom="xxl"
						size="sm"
						fullWidth
					>
						{t('global.apply')}
					</Button>
					<Button
						onClick={handleApply}
						hiddenFrom="xxl"
						size="xs"
						fullWidth
						data-testid="M001I1R1_Z5jdHi6B-button"
					>
						{t('global.apply')}
					</Button>
				</Box>
				<Box className={classes.containerSelected}>
					<Text
						data-testid="D1CtGXwC_C5cdHi64-button"
						onClick={handleClearSelectedItems}
						className={classes.textClearSelected}
					>
						{t('global.clear')}
					</Text>
				</Box>
			</Popover.Dropdown>
		</Popover>
	);
};
